/**
 * Scenario Center business content — the Chinese presentation layer.
 *
 * WHAT THIS IS
 *   The copy the redesigned Scenario Center renders: the five scenario cards, the six
 *   typical-issue explanations, and the official documents and incident cases each issue
 *   cites. Every issue is keyed to the fixed source-bound ScenarioTemplate by code
 *   (`HRF-HZ-*`), so the business view and the source-bound view name the same record.
 *
 * WHAT THIS IS NOT
 *   It is not a domain model, not a database seed, not an authorization and not an
 *   evidence substitute. `scenario-research-reference.ts` remains the authority for
 *   WHICH source entries an issue links to, and `consumer/source-bound-scenario.ts`
 *   remains the only reader. Nothing here creates a capability definition, a local
 *   asset, a mission, a readiness claim or a verified status.
 *
 * WHERE THE OFFICIAL FACTS COME FROM
 *   Document titles, publishers, dates and URLs are the ones registered in the delivery
 *   kit's `materials/pdf-sources.csv` and `materials/source-register.csv`. The editorial
 *   explanations are the reviewed drafts in `content/research-evidence-mapping.json`.
 *   An official 调查情况通报 is never presented as a complete investigation report, and a
 *   related building-fire case is never relabelled as a high-rise case.
 *
 * LINK STATUS (checked 2026-10-28 from this workstation)
 *   All registered URLs answered except the 江城区应急管理局 mirror of the 新余 report;
 *   that report is cited from its 应急管理部 publication instead. No PDF is bundled: the
 *   page links to the official original and states the link's nature.
 */

/** The five scenario cards. All five always render; none is hidden by content state. */
export type ScenarioContentState = 'REFERENCE' | 'IN_PREPARATION';

export interface ScenarioCardContent {
  readonly id: string;
  readonly name: string;
  readonly category: string;
  readonly state: ScenarioContentState;
  readonly stateLabel: string;
  readonly summary: string;
  readonly action: string;
  /** Short labels shown on the card. They describe the scenario, never a status. */
  readonly topics: readonly string[];
  /** The scenario's scope sentence. Also the detail page's one-line subtitle. */
  readonly scope: string;
  /** The directions the scenario is being prepared along. Empty for a formed scenario. */
  readonly directions: readonly string[];
}

export const scenarioCards: readonly ScenarioCardContent[] = [
  {
    id: 'high-rise-building-fire',
    name: '高层建筑火灾',
    category: '建筑火灾',
    state: 'REFERENCE',
    stateLabel: '场景参考',
    summary: '关注烟气蔓延、人员受困、疏散通道与消防设施。',
    action: '进入场景',
    topics: ['烟气蔓延', '人员受困', '通道受阻'],
    scope: '关注高层建筑中的烟气、人员、通道与消防设施问题。',
    directions: [],
  },
  {
    id: 'forest-fire',
    name: '森林火灾',
    category: '森林与自然灾害',
    state: 'REFERENCE',
    stateLabel: '研究参考',
    summary: '围绕林区火情发现、环境变化与区域协同整理资料。',
    action: '进入场景',
    topics: ['火情发现', '环境变化'],
    scope: '林区火情发现、火场环境变化与跨区域协同准备。',
    directions: ['林区火情发现与早期判断', '火场环境变化与区域协同'],
  },
  {
    id: 'chemical',
    name: '危化品事故',
    category: '危化与特殊事故',
    state: 'REFERENCE',
    stateLabel: '研究参考',
    summary: '关注物质身份、释放状态、人员暴露、火灾爆炸、影响范围与环境影响。',
    action: '进入场景',
    topics: ['物质身份', '泄漏与释放', '火灾爆炸'],
    scope: '危险化学品生产、储存、使用、经营、装卸、运输等环节的事故研究参考。',
    directions: [],
  },
  {
    id: 'transport',
    name: '交通与地下空间事故',
    category: '交通与地下空间',
    state: 'REFERENCE',
    stateLabel: '研究参考',
    summary: '关注隧道与地下空间火灾烟气、事故阻塞、人员疏散、积水设施、结构通行与通信。',
    action: '进入场景',
    topics: ['隧道火灾烟气', '事故阻塞', '积水与设施'],
    scope: '公路及城市道路隧道、地铁车站与区间、地下车库与通道等交通相关地下空间的事故研究参考。',
    directions: [],
  },
  {
    id: 'flood',
    name: '洪水与城市内涝',
    category: '水域与搜救',
    state: 'REFERENCE',
    stateLabel: '研究参考',
    summary: '关注暴雨雨情、河道洪水、地下空间倒灌、排水设施、人员安全与交通协同。',
    action: '进入场景',
    topics: ['雨情水位', '地下倒灌', '水域救援'],
    scope: '洪水与城市内涝中的雨情、水位、积水位置、地下空间、排水设施、人员安全与跨部门信息协同。',
    directions: [],
  },
] as const;

/** The one scenario whose typical issues are formed and readable today. */
export const formedScenarioId = 'high-rise-building-fire';

export function findScenarioCard(id: string): ScenarioCardContent | undefined {
  return scenarioCards.find((card) => card.id === id);
}

/* ------------------------------------------------------------------ official sources */

export type DocumentKind = '部门规章' | '标准规范' | '历史专项文件' | '地方应急指导资料';

export interface OfficialDocument {
  readonly id: string;
  readonly name: string;
  readonly publisher: string;
  readonly kind: DocumentKind;
  readonly url: string;
  readonly linkLabel: string;
  /** What the document is, in one sentence. Never an incident conclusion. */
  readonly note: string;
  /** What the scenario uses it for, and where its use stops. */
  readonly use: string;
  /** The issues this document is cited by. */
  readonly issues: readonly string[];
}

export const officialDocuments: readonly OfficialDocument[] = [
  {
    id: 'CN-HIGHRISE-MGMT',
    name: '高层民用建筑消防安全管理规定',
    publisher: '应急管理部',
    kind: '部门规章',
    url: 'https://www.mem.gov.cn/gk/zfxxgkpt/fdzdgknr/202106/t20210625_389980.shtml',
    linkLabel: '阅读官方原文',
    note: '规定高层民用建筑的消防安全职责、建筑消防资料、消防设施维护以及疏散通道和出口的管理要求。',
    use: '用于说明竖井封堵、防火门、疏散出口、避难空间和消防设施维护的管理依据；不构成具体建筑的结构安全结论。',
    issues: ['HRF-HZ-02', 'HRF-HZ-03', 'HRF-HZ-04', 'HRF-HZ-01', 'HRF-HZ-05', 'HRF-HZ-06'],
  },
  {
    id: 'CN-HIGHRISE-RISK-2022',
    name: '高层建筑重大火灾风险专项整治通知',
    publisher: '应急管理部',
    kind: '历史专项文件',
    url: 'https://www.mem.gov.cn/gk/zfxxgkpt/fdzdgknr/202303/t20230320_445229.shtml',
    linkLabel: '阅读官方原文',
    note: '2022—2023 年专项资料，汇总高层建筑竖向管井、防火分隔、疏散通道和消防设施等共性风险。',
    use: '仅作为风险背景引用，不代表当前正在开展的整治任务，也不作为某项能力的验证证据。',
    issues: ['HRF-HZ-01'],
  },
  {
    id: 'CN-GB55037-2022',
    name: '《建筑防火通用规范》GB 55037—2022',
    publisher: '江苏省消防救援总队（提供公开附件）',
    kind: '标准规范',
    url: 'https://js.119.gov.cn/202509/fe45189e7ac74137983ba8a111eea40c_c_0738bb1eadf249658e47.html',
    linkLabel: '阅读官方发布页',
    note: '国家标准文本；本页引用的规范副本由江苏省消防救援总队网站公开托管。',
    use: '用于说明结构耐火与防火分隔的设计要求；规范要求不等于既有建筑的现场状态。',
    issues: ['HRF-HZ-03', 'HRF-HZ-05'],
  },
  {
    id: 'CN-QINGPU-CURTAIN-WALL-GUIDE',
    name: '建筑玻璃幕墙应急指导手册',
    publisher: '上海市青浦区建设和管理委员会',
    kind: '地方应急指导资料',
    url: 'https://www.shqp.gov.cn/cons/cons/upload/202104/0408_161822_357.pdf',
    linkLabel: '阅读指导手册（PDF）',
    note: '地方应急指导资料，列出幕墙、窗扇和外装饰构件的常见险情类型与观察要点。',
    use: '用于说明外围构件异常应当记录什么；观察要点可借鉴，具体判断仍需现场核查，不能据此计算警戒距离。',
    issues: ['HRF-HZ-06'],
  },
] as const;

export type CaseKind = '官方调查情况通报' | '官方事故调查报告';

export interface OfficialCase {
  readonly id: string;
  readonly name: string;
  /** The incident date as the official material states it. */
  readonly date: string;
  readonly kind: CaseKind;
  readonly publisher: string;
  /** How the case relates to this scenario. Never a reclassification. */
  readonly classification: string;
  readonly url: string;
  readonly linkLabel: string;
  readonly summary: string;
  /** Where this case may and may not be used. */
  readonly use: string;
  readonly issues: readonly string[];
}

export const officialCases: readonly OfficialCase[] = [
  {
    id: 'CASE-NANJING-2024',
    name: '南京明尚西苑“2·23”火灾调查情况通报',
    date: '2024-02-23',
    kind: '官方调查情况通报',
    publisher: '江苏省消防救援总队',
    classification: '网页通报，不是完整调查报告',
    url: 'https://js.119.gov.cn/202411/jsxfww-menu-XXGK-tzgg_c_06f09db00ccc43919e1a.html',
    linkLabel: '阅读官方调查通报',
    summary:
      '通报指出电动自行车电池起火后引燃周边车辆和天井内可燃物，火焰与高温烟气在烟囱效应作用下进入部分住户室内。',
    use: '用于说明竖向空间中的火烟传播和消防设施维护问题；不得称为完整事故调查报告。',
    issues: ['HRF-HZ-02', 'HRF-HZ-01'],
  },
  {
    id: 'CASE-PUDONG-LONGYANG-2026',
    name: '上海浦东龙阳路“4·26”火灾事故调查报告',
    date: '2026-04-26',
    kind: '官方事故调查报告',
    publisher: '浦东新区政府网站公开',
    classification: '高层住宅案例',
    url: 'https://www.pudong.gov.cn/019012011/20260708/829495/22f58f9734ae4cd7b56ed35a490da648.pdf',
    linkLabel: '打开官方调查报告 PDF',
    summary:
      '事发建筑为 16 层高层住宅。报告分析了架空层与上部物业管理用房之间的局部连通、烟气沿楼梯间传播以及用房出口受阻的情况。',
    use: '用于说明局部楼梯间的烟气传播、防火分隔与出口条件；不扩展为整栋 16 层全部受烟气影响。',
    issues: ['HRF-HZ-02', 'HRF-HZ-04', 'HRF-HZ-01'],
  },
  {
    id: 'CASE-XINYU-2024',
    name: '江西新余佳乐苑“1·24”特别重大火灾事故调查报告',
    date: '2024-01-24',
    kind: '官方事故调查报告',
    publisher: '国务院事故调查组／应急管理部发布',
    classification: '相关建筑火灾案例，不属于高层住宅事故',
    url: 'https://www.mem.gov.cn/gk/zfxxgkpt/fdzdgknr/202409/W020240921581426179695.pdf',
    linkLabel: '打开官方调查报告 PDF',
    summary:
      '报告分析了建筑防火分隔缺失、烟气蔓延和逃生条件等因素，并记录了防盗网、广告牌、门及通道障碍对逃生和救援的影响。',
    use: '作为相关建筑火灾案例，用于说明防火分隔、疏散条件与资料准备的价值；不能归类为高层建筑火灾。',
    issues: ['HRF-HZ-05', 'HRF-HZ-04', 'HRF-HZ-03'],
  },
  {
    id: 'CASE-CHANGFENG-2023',
    name: '北京丰台长峰医院“4·18”重大火灾事故调查报告',
    date: '2023-04-18',
    kind: '官方事故调查报告',
    publisher: '国务院事故调查组／应急管理部',
    classification: '医疗建筑相关案例',
    url: 'https://www.mem.gov.cn/gk/zfxxgkpt/fdzdgknr/202310/W020240204579055057534.pdf',
    linkLabel: '打开官方调查报告 PDF',
    summary:
      '报告记录了可燃装修、防火分隔与固定消防设施问题，以及行动受限人员、通道条件和转移困难对事故后果的影响。',
    use: '用于说明人员行动能力与转移条件的重要性；不得把医疗建筑的人员特征套用到住宅建筑。',
    issues: ['HRF-HZ-03', 'HRF-HZ-01', 'HRF-HZ-05'],
  },
] as const;

export function findDocument(id: string): OfficialDocument | undefined {
  return officialDocuments.find((row) => row.id === id);
}

export function findCase(id: string): OfficialCase | undefined {
  return officialCases.find((row) => row.id === id);
}

/** The three groups the 依据与案例 page sorts by, in reading order. */
export type EvidenceGroup = '规定与标准' | '事故案例' | '技术说明';

export const evidenceGroupOrder: readonly EvidenceGroup[] = ['规定与标准', '事故案例', '技术说明'];

/**
 * Which group a source belongs to, derived from the kind the source registry already states.
 * Derived rather than stored a second time, so a document cannot be in two groups at once.
 */
export function evidenceGroupOf(kind: DocumentKind | CaseKind): EvidenceGroup {
  if (kind === '官方调查情况通报' || kind === '官方事故调查报告') return '事故案例';
  if (kind === '地方应急指导资料') return '技术说明';
  return '规定与标准';
}

/* ------------------------------------------------------------------ typical issues */

export interface IssueDocumentRef {
  readonly id: string;
  /** The article, chapter or report page the citation points at. */
  readonly locator: string;
  /** What this document actually supports for THIS issue. */
  readonly relation: string;
}

export interface IssueCaseRef {
  readonly id: string;
  /** What the official material established, for THIS issue. */
  readonly relation: string;
  /** Where to read it. Empty when the source gives no page to cite. */
  readonly locator: string;
}

export interface IssueContent {
  /** The source-bound risk code this issue is the business reading of. */
  readonly code: string;
  readonly key: string;
  readonly title: string;
  /** What the issue is, in two or three sentences. */
  readonly summary: string;
  /** What it can lead to, so the reader knows why it is on the list. */
  readonly impact: string;
  /** Why the issue is worth attention, with the official basis named. */
  readonly explanation: string;
  readonly documents: readonly IssueDocumentRef[];
  readonly cases: readonly IssueCaseRef[];
  /** What this page must not be read as concluding. */
  readonly boundary: string;
}

/**
 * The six typical issues, in reading order.
 *
 * The order is the user's confirmed presentation order, not a severity ranking and not
 * an operational priority. `code` is the source-bound record the business reading maps
 * onto; the information and capability sets are NOT repeated here — they come from
 * `issueRelations`, which is the single mapping authority.
 */
export const issueContent: readonly IssueContent[] = [
  {
    code: 'HRF-HZ-02',
    key: 'smoke',
    title: '烟气蔓延',
    summary:
      '高层建筑中的楼梯间、竖井等空间可能形成烟气传播路径。需要关注楼层之间的连通关系，以及烟气可能影响的区域。',
    impact: '烟气可能在起火层以外的区域造成能见度下降和吸入风险，影响人员疏散和进入条件。',
    explanation:
      '管理规定对竖向管井的封堵和检查门提出要求。南京明尚西苑和上海浦东龙阳路的官方调查材料，也分别记录了天井、楼梯间参与火烟传播的情况。',
    documents: [
      {
        id: 'CN-HIGHRISE-MGMT',
        locator: '第二十条',
        relation: '竖向管井封堵和检查门的管理要求',
      },
    ],
    cases: [
      {
        id: 'CASE-NANJING-2024',
        relation: '通报查明火焰与高温烟气经天井影响部分住户',
        locator: '调查组查明段',
      },
      {
        id: 'CASE-PUDONG-LONGYANG-2026',
        relation: '报告记录烟气沿楼梯间向上部物业管理用房传播',
        locator: '报告 PDF 第 11 页／正文第 8 页',
      },
    ],
    boundary: '案例说明的是已查明的事故经过，不代表当前建筑正在出现相同的传播路径。',
  },
  {
    code: 'HRF-HZ-03',
    key: 'people',
    title: '人员受困与疏散',
    summary: '人员所在位置、能否自行移动，以及可用的疏散条件，是理解受困问题需要先明确的信息。',
    impact: '人员位置与疏散条件不清楚时，无法判断需要何种搜索与转移支持，也无法评估疏散安排。',
    explanation:
      '管理规定对疏散出口和避难设施提出要求。北京丰台长峰医院调查报告记录了行动受限人员、通道条件和转移困难对事故后果的影响。',
    documents: [
      {
        id: 'CN-HIGHRISE-MGMT',
        locator: '第二十八、二十九条',
        relation: '疏散出口和避难设施的管理要求',
      },
      {
        id: 'CN-GB55037-2022',
        locator: '第 7.1.1 条',
        relation: '疏散设施需要与建筑和人员条件相适应',
      },
    ],
    cases: [
      {
        id: 'CASE-CHANGFENG-2023',
        relation: '报告记录行动受限人员与转移条件对事故后果的影响',
        locator: '报告 PDF 第 18 页／正文第 15 页',
      },
      {
        id: 'CASE-XINYU-2024',
        relation: '相关建筑火灾案例，涉及疏散条件受限',
        locator: '',
      },
    ],
    boundary:
      '不能把医疗建筑案例中的人员特征套用到住宅；人数未知时不得填 0，也不得显示为无人受困。',
  },
  {
    code: 'HRF-HZ-01',
    key: 'fire',
    title: '火势发展与蔓延',
    summary: '火源周边可燃物、建筑连通空间和消防设施状态，可能共同影响火势发展。',
    impact: '起火范围与蔓延条件不清楚时，无法判断需要何种抑制、观察与供水支持。',
    explanation:
      '官方事故材料显示，周边可燃物被引燃、防火分隔不足或消防设施未有效运行，可能在具体事故中叠加放大影响。',
    documents: [
      {
        id: 'CN-HIGHRISE-RISK-2022',
        locator: '正文风险说明、共性突出风险',
        relation: '高层建筑火灾蔓延的共性风险背景',
      },
      {
        id: 'CN-HIGHRISE-MGMT',
        locator: '第十九、二十、三十二条',
        relation: '可燃物管理、竖向管井封堵与消防设施维护要求',
      },
    ],
    cases: [
      {
        id: 'CASE-NANJING-2024',
        relation: '通报查明周边车辆和天井内可燃物参与蔓延',
        locator: '调查组查明段',
      },
      {
        id: 'CASE-PUDONG-LONGYANG-2026',
        relation: '报告记录车辆密集、防火分隔不足等蔓延因素',
        locator: '报告 PDF 第 11 页／正文第 8 页',
      },
      {
        id: 'CASE-CHANGFENG-2023',
        relation: '报告记录可燃装修、分隔和固定消防设施问题',
        locator: '报告 PDF 第 17 页／正文第 14 页',
      },
    ],
    boundary: '不把案例中的发展速度转换为通用预测，不自动生成蔓延时间或风险等级。',
  },
  {
    code: 'HRF-HZ-05',
    key: 'structure',
    title: '建筑结构与防火分隔',
    summary:
      '结构耐火条件与防火门、墙体、管井封堵的完整性需要分别了解。结构受损情况还需现场专业评估。',
    impact: '分隔完整性影响火烟传播范围；结构状态需要专业评估，不能由影像或图纸判断。',
    explanation:
      '规范分别规定结构耐火和防火分隔要求。事故调查中的分隔缺陷可以帮助理解火烟传播，但不能据此判断当前建筑会不会坍塌。',
    documents: [
      {
        id: 'CN-GB55037-2022',
        locator: '第 5.1.1 条',
        relation: '结构耐火的设计要求',
      },
      {
        id: 'CN-GB55037-2022',
        locator: '第 6.2.4、6.3.1—6.3.3 条',
        relation: '幕墙和竖井的防火分隔要求',
      },
      {
        id: 'CN-HIGHRISE-MGMT',
        locator: '第十四、二十、二十六条',
        relation: '建筑消防资料保存与竖向管井管理要求',
      },
    ],
    cases: [
      {
        id: 'CASE-XINYU-2024',
        relation: '报告记录共用楼梯缺少防火分隔的情况，不构成结构失稳结论',
        locator: '报告 PDF 第 13 页／正文第 10 页',
      },
      {
        id: 'CASE-CHANGFENG-2023',
        relation: '报告记录管井、防火门及防火分隔问题',
        locator: '报告 PDF 第 17 页／正文第 14 页',
      },
    ],
    boundary:
      '分隔问题与结构问题不能共用一个安全结论；不得用标题把尚未核实的状态直接定性为“失效”。',
  },
  {
    code: 'HRF-HZ-06',
    key: 'falling',
    title: '高处坠物',
    summary: '幕墙玻璃、外装饰构件或窗扇出现破损、松动、变形时，需要记录位置与可能影响的区域。',
    impact: '坠落物可能影响建筑外围的接近路线、集结区域和作业场地。',
    explanation:
      '青浦区公开的幕墙应急指导手册列出了这些常见险情和观察要点，可以借鉴；具体判断仍需现场核查。',
    documents: [
      {
        id: 'CN-QINGPU-CURTAIN-WALL-GUIDE',
        locator: '手册 PDF 第 3 页／正文第 1 页',
        relation: '幕墙、窗扇、外装饰构件的常见险情类型',
      },
      {
        id: 'CN-QINGPU-CURTAIN-WALL-GUIDE',
        locator: '手册 PDF 第 8 页／正文第 6 页',
        relation: '破损、松动、变形和连接部位的观察要点',
      },
      {
        id: 'CN-HIGHRISE-MGMT',
        locator: '第十九、二十一、二十二条',
        relation: '外保温、外装饰维护及妨碍救援障碍物的管理要求；不直接支持坠落半径',
      },
    ],
    cases: [],
    boundary:
      '本轮未取得经核验且适用的外围坠物事故案例，因此不展示案例卡片，也不自动计算警戒距离。',
  },
  {
    code: 'HRF-HZ-04',
    key: 'route',
    title: '疏散通道受阻',
    summary:
      '图纸上的连通关系和火灾时的可通行状态需要分开确认。烟气、门禁和堆物等都可能影响出口使用。',
    impact: '通道受阻会改变人员疏散路径和进入条件，并影响需要掌握的通道信息。',
    explanation:
      '管理规定要求保持疏散通道和出口畅通。浦东龙阳路调查报告进一步显示，局部用房出口经过起火架空层时，火势和烟气可能阻断通行。',
    documents: [
      {
        id: 'CN-HIGHRISE-MGMT',
        locator: '第二十八、二十九条',
        relation: '疏散通道、出口、门禁及防火门管理要求',
      },
    ],
    cases: [
      {
        id: 'CASE-PUDONG-LONGYANG-2026',
        relation: '报告记录物业管理用房出口经架空层后被火烟阻断',
        locator: '报告 PDF 第 12 页／正文第 9 页',
      },
      {
        id: 'CASE-XINYU-2024',
        relation: '相关建筑火灾案例，报告记录防盗网、广告牌等通道障碍影响逃生',
        locator: '报告 PDF 第 13 页／正文第 10 页',
      },
    ],
    boundary: '不根据案例给出向上、向下或固守的通用指令；不把历史图纸状态当作现场可用。',
  },
] as const;

export function findIssue(code: string): IssueContent | undefined {
  return issueContent.find((issue) => issue.code === code);
}

/** The issue the detail page opens on when the URL names none. */
export const defaultIssueCode = 'HRF-HZ-02';

/**
 * Business titles for the six readable issues.
 *
 * The source-bound projection keeps its own `name` for each risk record. This map is the
 * business reading of that record, so the front end can show 疏散通道受阻 where the
 * source record is titled 楼梯与疏散受阻 without renaming the source.
 */
export const issueBusinessTitles: Readonly<Record<string, string>> = Object.fromEntries(
  issueContent.map((issue) => [issue.code, issue.title]),
);

/**
 * The scope note the detail page shows once, so a reader knows what kind of content this is.
 */
export const scenarioContentNotice =
  '本页内容为场景参考：典型问题、需要掌握的信息与所需能力由平台依据官方公开资料整理，用于研究与准备。它不表示某次事故的现场事实，也不表示某项能力已经具备或某台装备已经可用。';
